import type { ReactNode } from 'react';
import { useContext, useMemo } from 'react';
import type { TooltipContextType } from '@visx/xychart';
import { TooltipContext as VisxTooltipContext } from '@visx/xychart';

import { useTooltipContext } from './TooltipContext';

interface PinnedTooltipProviderProps {
  children: ReactNode;
}

/**
 * Provider that intercepts visx's TooltipContext and provides frozen values when tooltip is pinned.
 *
 * This is the key to click-to-freeze: visx's Tooltip component reads from TooltipContext,
 * so by providing a wrapper context with frozen values, we can keep the tooltip
 * at a fixed position while the user moves their cursor.
 *
 * How it works:
 * 1. When NOT pinned: passes through the real visx context values (normal hover behavior)
 * 2. When pinned: returns frozen tooltipLeft, tooltipTop, tooltipData, tooltipOpen=true
 *
 * Must be placed inside XYChart (to access visx's TooltipContext) and outside Tooltip component.
 */
export function PinnedTooltipProvider({ children }: PinnedTooltipProviderProps) {
  const realVisxContext = useContext(VisxTooltipContext) as TooltipContextType<object> | null;
  const { isPinned, pinnedVisxTooltipData } = useTooltipContext();

  // Create intercepted context that freezes values when pinned
  const interceptedContext = useMemo(() => {
    if (!realVisxContext) {
      return null;
    }

    // When pinned and we have stored data, override with frozen values
    if (isPinned && pinnedVisxTooltipData) {
      return {
        ...realVisxContext,
        tooltipOpen: true,
        tooltipLeft: pinnedVisxTooltipData.tooltipLeft,
        tooltipTop: pinnedVisxTooltipData.tooltipTop,
        tooltipData: pinnedVisxTooltipData.tooltipData,
        // Keep showTooltip/hideTooltip as no-ops when pinned to prevent updates
        showTooltip: () => {},
        hideTooltip: () => {},
      };
    }

    // Not pinned - pass through real context
    return realVisxContext;
  }, [realVisxContext, isPinned, pinnedVisxTooltipData]);

  // Re-provide the context with intercepted values
  return (
    <VisxTooltipContext.Provider value={interceptedContext as TooltipContextType<object> | null}>
      {children}
    </VisxTooltipContext.Provider>
  );
}
